import fetchWithAuthRetry from "./fetchWithAuthRetry";
import { showMessage } from "../customhooks/useNotifier";

import environment from "../environments/environment";
const API_URL = environment.REACT_APP_BACKEND_API_BASE_URL;

async function handleResponse(response, successMessage, errorMessage) {
  let result;
  try {
    result = await response.json();
  } catch {
    throw new Error("Invalid server response");
  }

  if (!response.ok || result?.success === false) {
    // special case: no Google connection
    if (result?.message === "No Google connection found") {
      return null; // just return null instead of error/toast
    }

    // special case: Workiz JobTypes not available
    if (result?.message?.includes("Workiz getJobTypes failed")) {
      console.error("⚠️ Workiz job types error:", result?.message);
      return null; // suppress toast + error, just return null
    }

    const msg = result?.message || errorMessage;
    showMessage(msg, "error");
    throw new Error(msg);
  }

  if (successMessage) {
    showMessage(result.message || successMessage, "success");
  }

  return result.data ?? result ?? null;
}

const ActionsService = {
  getConnection: async (kbId) => {
    const response = await fetchWithAuthRetry(
      `${API_URL}/api/v1/actions/get-connection?kbid=${kbId}`,
      { method: "GET", headers: { "Content-Type": "application/json" } },
    );
    return handleResponse(response, null, "Failed to fetch connection");
  },
  // getConnectionByUserId: async () => {
  //   const response = await fetchWithAuthRetry(
  //     `${API_URL}/api/v1/actions/get-connection-by-user-id`,
  //     { method: "GET", headers: { "Content-Type": "application/json" } }
  //   );
  //   return handleResponse(response, null, "Failed to fetch connection");
  // },
  getAuthUrl: async (kbId, callbackType) => {
    if (!kbId) throw new Error("kbId is required");

    const url = new URL(`${API_URL}/api/v1/actions/auth-url`);
    url.searchParams.append("kbId", kbId);
    url.searchParams.append("callbackType", callbackType);

    const response = await fetchWithAuthRetry(url.toString(), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    return handleResponse(response, null, "Failed to start Google OAuth flow");
  },

  findAvailability: async (data) => {
    const response = await fetchWithAuthRetry(
      `${API_URL}/api/v1/actions/find-availability`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );
    return handleResponse(response, null, "Failed to fetch availability");
  },
  listEvents: async (data) => {
    const response = await fetchWithAuthRetry(
      `${API_URL}/api/v1/actions/list-events`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );
    return handleResponse(response, null, "Failed to fetch events");
  },
  createEvent: async (data) => {
    const response = await fetchWithAuthRetry(
      `${API_URL}/api/v1/actions/create-event`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );
    return handleResponse(
      response,
      "Event created successfully",
      "Failed to create event",
    );
  },
  disconnect: async () => {
    const response = await fetchWithAuthRetry(
      `${API_URL}/api/v1/actions/disconnect`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      },
    );
    return handleResponse(
      response,
      "Disconnected successfully",
      "Failed to disconnect",
    );
  },
  updateEvent: async (data) => {
    const response = await fetchWithAuthRetry(
      `${API_URL}/api/v1/actions/update-event`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );
    return handleResponse(
      response,
      "Event updated successfully",
      "Failed to update event",
    );
  },
  deleteEvent: async (data) => {
    const response = await fetchWithAuthRetry(
      `${API_URL}/api/v1/actions/delete-event`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    );
    return handleResponse(
      response,
      "Event deleted successfully",
      "Failed to delete event",
    );
  },
  createPublicEvent: async (payload) => {
    const response = await fetchWithAuthRetry(
      `${API_URL}/api/v1/actions/create-public-event`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: payload.userId,
          calendarId: payload.calendarId || "primary",
          summary: payload.summary,
          description: payload.description || "",
          start: payload.start,
          end: payload.end,
          attendeeEmail: payload.attendeeEmail,
          attendeeName: payload.attendeeName || "",
        }),
      },
    );

    return handleResponse(
      response,
      "Event created & invite sent successfully",
      "Failed to create public event",
    );
  },

  // ===== Workiz =====
  WorkizgetConnection: async (userId) => {
    try {
      const res = await fetchWithAuthRetry(
        `${API_URL}/api/v1/workiz/get-connection`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId }),
        },
      );

      if (res.status === 404) return null; // no connection
      return handleResponse(res, null, "Failed to fetch Workiz connection");
    } catch (err) {
      showMessage(err.message || "Failed to fetch Workiz connection", "error");
      console.error("❌ WorkizgetConnection error:", err);
      throw err;
    }
  },

  Workizconnect: async (userId, apiKey, apiSecret) => {
    try {
      const res = await fetchWithAuthRetry(`${API_URL}/api/v1/workiz/connect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, apiKey, apiSecret }),
      });
      return handleResponse(
        res,
        "Connected to Workiz",
        "Failed to connect Workiz",
      );
    } catch (err) {
      console.error("❌ Workizconnect error:", err);
      throw err;
    }
  },

  Workizdisconnect: async (userId) => {
    try {
      const res = await fetchWithAuthRetry(
        `${API_URL}/api/v1/workiz/disconnect`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId }),
        },
      );
      return handleResponse(
        res,
        "Disconnected from Workiz successfully",
        "Failed to disconnect Workiz",
      );
    } catch (err) {
      console.error("❌ Workizdisconnect error:", err);
      throw err;
    }
  },

  WorkizlistJobs: async (userId) => {
    try {
      const url = new URL(`${API_URL}/api/v1/workiz/jobs`);
      url.searchParams.append("userId", userId);

      const res = await fetchWithAuthRetry(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      return handleResponse(res, null, "Failed to fetch Workiz jobs");
    } catch (err) {
      console.error("❌ WorkizlistJobs error:", err);
      throw err;
    }
  },

  WorkizcreateJob: async (userId, jobData) => {
    try {
      // ✅ Required fields
      const payload = {
        Email: jobData.Email?.trim(),
        FirstName: jobData.FirstName?.trim(),
        Address: jobData.Address?.trim(),
        City: jobData.City?.trim(),
        State: jobData.State?.trim(),
        PostalCode: jobData.PostalCode?.trim(),
        JobType: jobData.JobType || jobData.title || "General",
        JobDateTime: formatJobDateTime(jobData.JobDateTime),
        JobEndDateTime: formatJobDateTime(jobData.JobEndDateTime),
      };

      // ✅ Validate required fields
      const requiredFields = Object.keys(payload);
      for (const field of requiredFields) {
        if (!payload[field])
          throw new Error(`Missing required field: ${field}`);
      }

      // ✅ Debug
      if (process.env.NODE_ENV !== "production") {
        console.log("🚀 Workiz Create Job Payload:", payload);
      }

      // ✅ API Call
      const res = await fetchWithAuthRetry(`${API_URL}/api/v1/workiz/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, jobData: payload }),
      });

      return handleResponse(
        res,
        "✅ Job created successfully!",
        "❌ Failed to create job",
      );
    } catch (err) {
      console.error("❌ WorkizcreateJob error:", err);
      throw err;
    }
  },
  WorkizupdateJob: async (userId, jobData) => {
    try {
      if (!jobData.UUID) throw new Error("Missing required field: UUID");

      const payload = {
        UUID: jobData.UUID,
        Email: jobData.Email?.trim(),
        FirstName: jobData.FirstName?.trim(),
        Address: jobData.Address?.trim(),
        City: jobData.City?.trim(),
        State: jobData.State?.trim(),
        PostalCode: jobData.PostalCode?.trim(),
        JobType: jobData.JobType || jobData.title || "General",
        JobDateTime: formatJobDateTime(jobData.JobDateTime),
        JobEndDateTime: formatJobDateTime(jobData.JobEndDateTime),
      };

      // Remove empty fields (only send what needs update)
      Object.keys(payload).forEach(
        (key) =>
          (payload[key] === undefined || payload[key] === "") &&
          delete payload[key],
      );

      if (process.env.NODE_ENV !== "production") {
        console.log("🛠 Workiz Update Job Payload:", payload);
      }

      const res = await fetchWithAuthRetry(
        `${API_URL}/api/v1/workiz/job-update`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, jobData: payload }),
        },
      );

      return handleResponse(
        res,
        "✅ Job updated successfully!",
        "❌ Failed to update job",
      );
    } catch (err) {
      console.error("❌ WorkizupdateJob error:", err);
      throw err;
    }
  },
  WorkizdeleteJob: async (userId, job) => {
    try {
      if (!job.UUID) throw new Error("Job UUID is required to unassign");
      if (!job.User) throw new Error("User is required to unassign job");

      const payload = {
        UUID: job.UUID,
        User: job.User,
      };

      const res = await fetchWithAuthRetry(
        `${API_URL}/api/v1/workiz/job-unassign`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, unassignData: payload }),
        },
      );

      return handleResponse(
        res,
        `✅ User ${payload.User} unassigned successfully from job ${payload.UUID}`,
        `❌ Failed to unassign user ${payload.User} from job ${payload.UUID}`,
      );
    } catch (err) {
      console.error("❌ WorkizdeleteJob (unassign) error:", err);
      throw err;
    }
  },
  WorkizgetJobTypes: async (userId) => {
    try {
      const res = await fetchWithAuthRetry(
        `${API_URL}/api/v1/workiz/job-types`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId }),
        },
      );
      return handleResponse(res, null, "Failed to fetch Workiz job types");
    } catch (err) {
      console.error("❌ WorkizgetJobTypes error:", err);
      throw err;
    }
  },

  // ========== Email Leads ==========
  createEmailLead: async (data) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/actions/email-leads`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );

      return handleResponse(
        response,
        "Email lead created successfully",
        "Failed to create email lead",
      );
    } catch (error) {
      console.error("❌ createEmailLead error:", error);
      throw error;
    }
  },

  getEmailLeads: async (status) => {
    try {
      const url = new URL(`${API_URL}/api/v1/actions/email-leads`);
      if (status) {
        url.searchParams.append("status", status);
      }
      const response = await fetchWithAuthRetry(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const result = await handleResponse(
        response,
        null,
        "Failed to fetch email leads",
      );
      // Backend returns { success: true, data: [...] }, extract data array
      return result?.data || result || [];
    } catch (err) {
      console.error("❌ getEmailLeads error:", err);
      // Don't throw on error, return empty array to show empty state
      return [];
    }
  },

  updateEmailLeadStatus: async (leadId, status) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/actions/email-leads/${leadId}/status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        },
      );
      return handleResponse(
        response,
        "Email lead status updated successfully",
        "Failed to update email lead status",
      );
    } catch (err) {
      console.error("❌ updateEmailLeadStatus error:", err);
      throw err;
    }
  },

  searchEmailLeads: async (filters) => {
    try {
      const url = new URL(`${API_URL}/api/v1/actions/email-leads/search`);

      Object.entries(filters).forEach(([key, val]) => {
        if (val) url.searchParams.append(key, val);
      });

      const response = await fetchWithAuthRetry(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const result = await handleResponse(
        response,
        null,
        "Failed to search email leads",
      );

      return result?.data || result || [];
    } catch (err) {
      console.error("❌ searchEmailLeads error:", err);
      return [];
    }
  },

  updateEmailLead: async (leadId, data) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/actions/email-leads/${leadId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );

      return handleResponse(
        response,
        "Email lead updated successfully",
        "Failed to update email lead",
      );
    } catch (err) {
      console.error("❌ updateEmailLead error:", err);
      throw err;
    }
  },

  // ========== Scheduled Meetings ==========
  createScheduledMeeting: async (data) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/actions/scheduled-meetings`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );
      return handleResponse(
        response,
        "Scheduled meeting saved successfully",
        "Failed to create scheduled meeting",
      );
    } catch (err) {
      console.error("❌ createScheduledMeeting error:", err);
      throw err;
    }
  },

  getScheduledMeetings: async (upcomingOnly = true) => {
    try {
      const url = new URL(`${API_URL}/api/v1/actions/scheduled-meetings`);
      url.searchParams.append("upcomingOnly", upcomingOnly.toString());
      const response = await fetchWithAuthRetry(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const result = await handleResponse(
        response,
        null,
        "Failed to fetch scheduled meetings",
      );
      // Backend returns { success: true, data: [...] }, extract data array
      return result?.data || result || [];
    } catch (err) {
      console.error("❌ getScheduledMeetings error:", err);
      // Don't throw on error, return empty array
      return [];
    }
  },

  deactivateScheduledMeeting: async (meetingId) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/actions/scheduled-meetings/${meetingId}/deactivate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );
      return handleResponse(
        response,
        "Meeting deactivated successfully",
        "Failed to deactivate meeting",
      );
    } catch (err) {
      console.error("❌ deactivateScheduledMeeting error:", err);
      throw err;
    }
  },
};

function formatJobDateTime(dateInput) {
  if (!dateInput) return "";
  try {
    const date = new Date(dateInput);
    // Example: "2025-10-09 14:30:00"
    const pad = (n) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate(),
    )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds(),
    )}`;
  } catch {
    return "";
  }
}

export default ActionsService;
